/**
 * Scoped storage utilities for safe localStorage/sessionStorage management
 */

const AUTH_STORAGE_PREFIX = 'mira_auth:';
const APP_STORAGE_PREFIX = 'mira_app:';

/**
 * Clear all auth-related storage (scoped to auth prefix)
 */
export function clearAuthStorage() {
  // Clear from localStorage
  const localKeys = Object.keys(localStorage);
  localKeys.forEach(key => {
    if (key.startsWith(AUTH_STORAGE_PREFIX) ||
        key.startsWith('mira_chat_session_') ||
        key === 'token' ||
        key === 'user' ||
        key === 'userRole' ||
        key === 'stytch_session_token' ||
        key === 'stytch_user_data' ||
        key === 'expectedUserRole' ||
        key === 'mira_active_tabs' ||
        key === 'mira_session_sync') {
      localStorage.removeItem(key);
    }
  });

  // Clear from sessionStorage
  const sessionKeys = Object.keys(sessionStorage);
  sessionKeys.forEach(key => {
    if (key.startsWith(AUTH_STORAGE_PREFIX) ||
        key.startsWith('processing_token_') ||
        key === 'token' ||
        key === 'user' ||
        key === 'userRole' ||
        key === 'stytch_session_token' ||
        key === 'stytch_user_data' ||
        key === 'mira_active_chat_session' ||
        key === 'mira_fresh_login') {
      sessionStorage.removeItem(key);
    }
  });
}

/**
 * Clear all app storage (non-auth)
 */
export function clearAppStorage() {
  const localKeys = Object.keys(localStorage);
  localKeys.forEach(key => {
    if (key.startsWith(APP_STORAGE_PREFIX)) {
      localStorage.removeItem(key);
    }
  });
}

/**
 * Set auth-related data in localStorage
 * @param {string} key - Storage key (will be prefixed)
 * @param {any} value - Value to store
 */
export function setAuthData(key, value) {
  const prefixedKey = `${AUTH_STORAGE_PREFIX}${key}`;
  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(prefixedKey, serialized);
  } catch (error) {
    console.error(`[Storage] Failed to set ${key}:`, error);
  }
}

/**
 * Get auth-related data from localStorage
 * @param {string} key - Storage key (will be prefixed)
 * @returns {any} Parsed value or null
 */
export function getAuthData(key) {
  const prefixedKey = `${AUTH_STORAGE_PREFIX}${key}`;
  try {
    const item = localStorage.getItem(prefixedKey);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error(`[Storage] Failed to get ${key}:`, error);
    return null;
  }
}

/**
 * Set app data in localStorage
 * @param {string} key - Storage key (will be prefixed)
 * @param {any} value - Value to store
 */
export function setAppData(key, value) {
  const prefixedKey = `${APP_STORAGE_PREFIX}${key}`;
  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(prefixedKey, serialized);
  } catch (error) {
    console.error(`[Storage] Failed to set ${key}:`, error);
  }
}

/**
 * Get app data from localStorage
 * @param {string} key - Storage key (will be prefixed)
 * @returns {any} Parsed value or null
 */
export function getAppData(key) {
  const prefixedKey = `${APP_STORAGE_PREFIX}${key}`;
  try {
    const item = localStorage.getItem(prefixedKey);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error(`[Storage] Failed to get ${key}:`, error);
    return null;
  }
}

/**
 * Set temporary session data
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 * @param {number} ttlMs - Time to live in milliseconds (optional)
 */
export function setSessionData(key, value, ttlMs = null) {
  try {
    const data = {
      value,
      timestamp: Date.now(),
      ttl: ttlMs
    };
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error(`[Storage] Failed to set session data ${key}:`, error);
  }
}

/**
 * Get temporary session data
 * @param {string} key - Storage key
 * @returns {any} Value or null if expired/missing
 */
export function getSessionData(key) {
  try {
    const item = sessionStorage.getItem(key);
    if (!item) return null;

    const data = JSON.parse(item);

    // Check TTL if set
    if (data.ttl) {
      const age = Date.now() - data.timestamp;
      if (age > data.ttl) {
        sessionStorage.removeItem(key);
        return null;
      }
    }

    return data.value;
  } catch (error) {
    console.error(`[Storage] Failed to get session data ${key}:`, error);
    return null;
  }
}

/**
 * Clear expired session data
 */
export function clearExpiredSessionData() {
  const keys = Object.keys(sessionStorage);
  const now = Date.now();

  keys.forEach(key => {
    try {
      const item = sessionStorage.getItem(key);
      if (item) {
        const data = JSON.parse(item);
        if (data.ttl && data.timestamp) {
          const age = now - data.timestamp;
          if (age > data.ttl) {
            sessionStorage.removeItem(key);
          }
        }
      }
    } catch (error) {
      // Ignore parse errors for non-JSON items
    }
  });
}
